// Users.jsx
import React, { useEffect, useState } from "react";
import AddModal from "../components/AddModal";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import { API_BASE_URL } from "../config";

import "../styles/users.scss";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Button,
  Box,
  TableSortLabel,
  Menu,
  MenuItem,
} from "@mui/material";

import {
  OtherHousesOutlinedIcon,
  PeopleAltOutlinedIcon,
  AccountTreeOutlinedIcon,
  AssignmentTurnedInOutlinedIcon,
  QueryBuilderOutlinedIcon,
  AddTaskOutlinedIcon,
  CloudUploadOutlinedIcon,
  SettingsOutlinedIcon,
  LogoutIcon,
  CommentOutlinedIcon,
} from "../access/icons/Icons";

import { MoreVert } from "@mui/icons-material";

function Users({ refreshNotifications }) {
  const [users, setUsers] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const [modalOpen, setModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuUser, setMenuUser] = useState(null);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        setUsers(data);
      } else {
        setUsers([]); // fallback ako nije niz
        console.error("Expected array, got:", data);
      }
    } catch (error) {
      console.error("Failed to fetch users", error);
      setUsers([]); // fallback ako fetch pukne
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/users/${userToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          credentials: "include",
        }
      );

      if (res.ok) {
        fetchUsers();
        refreshNotifications?.();
        setModalOpen(false);
        setUserToDelete(null);
      } else {
        alert("Failed to delete user");
      }
    } catch (err) {
      console.error("Delete user failed", err);
      alert("Something went wrong while deleting user.");
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedUsers = [...users].sort((a, b) => {
    if (a[orderBy] < b[orderBy]) return order === "asc" ? -1 : 1;
    if (a[orderBy] > b[orderBy]) return order === "asc" ? 1 : -1;
    return 0;
  });

  const handleMenuOpen = (event, user) => {
    setMenuAnchor(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuUser(null);
  };

  const handleEditClick = () => {
    setEditUser(menuUser);
    setShowSidebar(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setUserToDelete(menuUser);
    setModalOpen(true);
    handleMenuClose();
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <>
      <Box className="users-page">
        <Box
          className="header"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" fontWeight={500}>
            Users
          </Typography>
          <div className="add-button" onClick={() => setShowSidebar(true)}>
            <PeopleAltOutlinedIcon /> Add user
          </div>
        </Box>

        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Table className="table">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === "name" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === "email" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "email"}
                    direction={orderBy === "email" ? order : "asc"}
                    onClick={() => handleSort("email")}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === "role" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "role"}
                    direction={orderBy === "role" ? order : "asc"}
                    onClick={() => handleSort("role")}
                  >
                    Role
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === "status" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "status"}
                    direction={orderBy === "status" ? order : "asc"}
                    onClick={() => handleSort("status")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ textTransform: "capitalize" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(sortedUsers) && sortedUsers.length > 0 ? (
                sortedUsers.map((user, i) => (
                  <TableRow key={user.id || i}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>

                    <TableCell>
                      <Typography
                        size="small"
                        variant="body2"
                        color={user.role === "admin" ? "primary" : "default"}
                        fontWeight={user.role === "admin" ? 500 : 500}
                      >
                        {user.role}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        size="small"
                        variant="body2"
                        color={user.status === "active" ? "success" : "error"}
                        fontWeight={user.status === "admin" ? 500 : 500}
                      >
                        {user.status}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(event) => handleMenuOpen(event, user)}
                        aria-controls={menuAnchor ? "user-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={menuAnchor ? "true" : undefined}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Menu
          id="user-menu"
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleEditClick}>Edit</MenuItem>
          <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
        </Menu>

        <AddModal
          open={showSidebar}
          onClose={() => {
            setShowSidebar(false);
            setEditUser(null);
          }}
          onUserAdded={() => {
            setShowSidebar(false);
            setEditUser(null);
            fetchUsers();
            refreshNotifications?.();
          }}
          editData={editUser}
          refreshNotifications={refreshNotifications}
        />
        <ConfirmDeleteModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setUserToDelete(null);
          }}
          onConfirm={handleDelete}
          itemName={userToDelete?.name || "user"}
        />
      </Box>
    </>
  );
}

export default Users;
